import { IMasterBuildsHook } from '@models';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useMasterBuilds = (): IMasterBuildsHook => {
  const { masterBuildStore } = useStore();
  const {
    builds,
    totalCount,
    loading,
    saving,
    currentPage,
    skipCount,
    maxResultCount,
    setSkipCount,
    setCurrentPage,
  } = masterBuildStore;

  const [keyword, setKeyword] = useState<string>('');
  const [customer, setCustomer] = useState<string>('');
  const [registrationNumber, setRegistrationNumber] = useState<string>('');
  const [vin, setVin] = useState<string>('');
  const [status, setStatusValue] = useState<string[]>([]);
  const [datePreset, setDatePreset] = useState<string | undefined>();
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();
  const [createdPreset, setCreatedPreset] = useState<string | undefined>();
  const [createdCustomFrom, setCreatedCustomFrom] = useState<string | undefined>();
  const [createdCustomTo, setCreatedCustomTo] = useState<string | undefined>();
  // ABP sorting expression from the table's date columns, e.g. 'DropOffDate DESC'.
  const [sorting, setSorting] = useState<string | undefined>();

  // Same preset machinery the statements and invoices lists use, so "This week" means the same
  // thing everywhere. Resolved against the build's drop-off rather than its creation date.
  const { startAt: dropOffFrom, endAt: dropOffTo } = useMemo(
    () =>
      datePreset === 'custom'
        ? { startAt: customFrom, endAt: customTo }
        : getDateRangeFromPreset(datePreset),
    [datePreset, customFrom, customTo]
  );

  // The same machinery again, resolved against CreationTime rather than the drop-off.
  const { startAt: createdFrom, endAt: createdTo } = useMemo(
    () =>
      createdPreset === 'custom'
        ? { startAt: createdCustomFrom, endAt: createdCustomTo }
        : getDateRangeFromPreset(createdPreset),
    [createdPreset, createdCustomFrom, createdCustomTo]
  );

  // The API takes statuses comma-separated, matching the invoice and statement lists.
  const request = useMemo(
    () => ({
      keyword,
      customer,
      registrationNumber,
      vin,
      status: status.length ? status.join(',') : null,
      dropOffFrom,
      dropOffTo,
      createdFrom,
      createdTo,
      maxResultCount,
      skipCount,
      sorting,
    }),
    [
      keyword,
      customer,
      registrationNumber,
      vin,
      status,
      dropOffFrom,
      dropOffTo,
      createdFrom,
      createdTo,
      maxResultCount,
      skipCount,
      sorting,
    ]
  );

  useEffect(() => {
    masterBuildStore.getAll(request);
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    masterBuildStore.getAll(request);
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Every filter resets paging, otherwise a narrowed result set lands on an empty page. */
  const makeFilter = useCallback(
    (setter: (value: string) => void) =>
      debounce((text: string) => {
        setter(text);
        setSkipCount(0);
        setCurrentPage(1);
      }, 500),
    [setSkipCount, setCurrentPage]
  );

  const handleSortChange = useCallback(
    (value?: string) => {
      setSorting(value);
      setSkipCount(0);
      setCurrentPage(1);
    },
    [setSkipCount, setCurrentPage]
  );

  const handleKeywordSearch = useMemo(() => makeFilter(setKeyword), [makeFilter]);
  const handleCustomerSearch = useMemo(() => makeFilter(setCustomer), [makeFilter]);
  const handleRegistrationSearch = useMemo(() => makeFilter(setRegistrationNumber), [makeFilter]);
  const handleVinSearch = useMemo(() => makeFilter(setVin), [makeFilter]);

  const setStatus = useCallback(
    (value: string[]) => {
      setStatusValue(value);
      setSkipCount(0);
      setCurrentPage(1);
    },
    [setSkipCount, setCurrentPage]
  );

  // Not debounced - both come from a picker, one deliberate change at a time.
  const handleDatePresetChange = useCallback(
    (preset: string) => {
      setDatePreset(preset || undefined);
      if (preset !== 'custom') {
        setCustomFrom(undefined);
        setCustomTo(undefined);
      }
      setSkipCount(0);
      setCurrentPage(1);
    },
    [setSkipCount, setCurrentPage]
  );

  const handleCustomDateRange = useCallback(
    (from?: string, to?: string) => {
      setCustomFrom(from);
      setCustomTo(to);
      setSkipCount(0);
      setCurrentPage(1);
    },
    [setSkipCount, setCurrentPage]
  );

  const handleCreatedPresetChange = useCallback(
    (preset: string) => {
      setCreatedPreset(preset || undefined);
      if (preset !== 'custom') {
        setCreatedCustomFrom(undefined);
        setCreatedCustomTo(undefined);
      }
      setSkipCount(0);
      setCurrentPage(1);
    },
    [setSkipCount, setCurrentPage]
  );

  const handleCreatedCustomRange = useCallback(
    (from?: string, to?: string) => {
      setCreatedCustomFrom(from);
      setCreatedCustomTo(to);
      setSkipCount(0);
      setCurrentPage(1);
    },
    [setSkipCount, setCurrentPage]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await masterBuildStore.delete({ id });
      refresh();
    },
    [masterBuildStore, refresh]
  );

  const fetchAllForExport = useCallback(
    () => masterBuildStore.fetchAllForExport(request),
    [masterBuildStore, request]
  );

  return {
    loading,
    saving,
    dataSource: Array.from(builds.values()),
    totalCount,
    currentPage,
    maxResultCount,
    handlePageChange: masterBuildStore.handlePageChange,
    handleSortChange,
    sorting,
    handleKeywordSearch,
    handleCustomerSearch,
    handleRegistrationSearch,
    handleVinSearch,
    setStatus,
    datePreset,
    customFrom,
    customTo,
    handleDatePresetChange,
    handleCustomDateRange,
    createdPreset,
    createdCustomFrom,
    createdCustomTo,
    handleCreatedPresetChange,
    handleCreatedCustomRange,
    handleDelete,
    refresh,
    fetchAllForExport,
  };
};
